"use client";

import React from "react";
import { Building2, CircleCheck as CheckCircle2, Clock, Globe as Globe2, Loader as Loader2, Mail, MapPin, Phone, Send, ShieldCheck } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
interface ContactDetail {
  iconName: string;
  title: string;
  detail: string;
  subtext: string;
}
const contactDetails: ContactDetail[] = [{
  iconName: "mail",
  title: "Direct Inquiries",
  detail: "info@antech.ie (IE) / info@antechsolutions.co.uk (UK)",
  subtext: "Technical specialist response within 24 hours"
}, {
  iconName: "phone",
  title: "Phone Support",
  detail: "+353 (0)51 392 391 (IE) / +44 (0)20 346 898 13 (UK)",
  subtext: "Monday to Friday, 8:30 AM – 5:30 PM"
}, {
  iconName: "mapPin",
  title: "Coverage Area",
  detail: "Ireland & United Kingdom",
  subtext: "On-site support and field service engineering"
}];
const assurances = [{
  title: "Regulated Industry Compliance",
  description: "Support tailored for GMP, GLP, and ISO regulated laboratories."
}, {
  title: "Application-Led Specialists",
  description: "Direct consultation with qualified chemometrics and instrumentation engineers."
}, {
  title: "Confidentiality Assured",
  description: "All technical specifications and sample protocols protected under strict NDA."
}];
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center rounded-xl border border-border bg-secondary/50 p-8 text-center sm:p-12">
        <div className="mb-4 flex size-14 items-center justify-center rounded-full bg-primary/10 text-primary">
          <CheckCircle2 className="size-8" />
        </div>
        <h3 className="font-default text-2xl font-bold text-foreground">
          Specification Received
        </h3>
        <p className="mt-2 max-w-md text-base text-muted-foreground">
          {successMessage || "Thank you. Our analytical specialists will review your requirements and get in touch within one business day."}
        </p>
        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <Button type="button" onClick={resetForm} variant="outline" className="border-border text-foreground hover:bg-accent hover:text-accent-foreground">
            Submit Another Request
          </Button>
          <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="/SolutionsPage">Explore Solutions</Link>
          </Button>
        </div>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <FormField control={control} name="name" rules={{
        required: "Please enter your name",
        minLength: {
          value: 2,
          message: "Name must be at least 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground">
                Name <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <Input placeholder="John Doe" className="bg-background text-foreground" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="work_email" rules={{
        required: "Work email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid work email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground">
                Work Email <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" className="bg-background text-foreground" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <FormField control={control} name="company_name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground">Company Name</FormLabel>
              <FormControl>
                <Input placeholder="Acme Corp" className="bg-background text-foreground" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone_number" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+44 20 1234 5678" className="bg-background text-foreground" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Please provide details about your requirement or application",
      minLength: {
        value: 10,
        message: "Please include at least 10 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground">
              Message <span className="text-destructive">*</span>
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Describe your process, target sample material, testing throughput, or analytical specification requirements..." rows={5} className="bg-background text-foreground" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3.5 text-sm text-destructive">
          <p className="font-medium">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-primary py-3 text-base font-semibold text-primary-foreground shadow-sm transition-opacity hover:bg-primary/90 disabled:opacity-60" size="lg">
        {isSubmitting ? <>
            <Loader2 className="mr-2 size-5 animate-spin" />
            Transmitting Request...
          </> : <>
            <Send className="mr-2 size-5" />
            Send Inquiry
          </>}
      </Button>

      <p className="text-center text-xs text-muted-foreground">
        By submitting this form, you agree to our confidential application review standards. Your data is never shared.
      </p>
    </Form>;
}
export default function ContactForm() {
  const shouldReduceMotion = useReducedMotion();
  const getContactIcon = (type: string) => {
    switch (type) {
      case "mail":
        return <Mail className="size-5 text-primary" />;
      case "phone":
        return <Phone className="size-5 text-primary" />;
      case "mapPin":
        return <MapPin className="size-5 text-primary" />;
      default:
        return <Globe2 className="size-5 text-primary" />;
    }
  };
  return <section id="contactform" data-nav="light" className="bg-background py-16 md:py-24 text-foreground relative overflow-hidden">
      <div className="page-container relative z-10">
        <div className="mx-auto max-w-3xl text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary px-3.5 py-1 text-xs font-semibold uppercase tracking-wider text-secondary-foreground mb-4">
            <Building2 className="size-3.5 text-primary" />
            <span>Consultation & Support</span>
          </div>
          <h2 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl">
            Contact Us - Antech Solutions
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
            Connect directly with our technical team for application feasibility, analytical instrumentation quotes, or qualification lifecycle support across Ireland and the UK.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12 items-start">
          {/* Left Column: Context & Support Details */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="lg:col-span-5 flex flex-col gap-8">
            <div className="space-y-4">
              <h3 className="font-default text-2xl font-bold text-foreground">
                Analytical Technology & Validation Experts
              </h3>
              <p className="text-sm leading-relaxed text-muted-foreground">
                Whether you require process analytical technology (PAT), NIR spectrometers, powder testing instruments, or solvent recovery systems, our team is equipped to support your regulated workflow.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4">
              {contactDetails.map((item, index) => <div key={item.title} data-index={index} className="flex items-start gap-4 rounded-lg border border-border bg-card p-4 text-card-foreground shadow-xs transition-[border-color,box-shadow] hover:border-primary/40 hover:shadow-sm">
                  <div className="flex size-10 shrink-0 items-center justify-center rounded-md bg-secondary text-secondary-foreground">
                    {getContactIcon(item.iconName)}
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground">
                      {item.title}
                    </h4>
                    <p className="text-sm font-medium text-foreground/90">
                      {item.detail}
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {item.subtext}
                    </p>
                  </div>
                </div>)}
            </div>

            <div className="rounded-xl border border-border bg-secondary/40 p-5">
              <h4 className="flex items-center gap-2 text-sm font-semibold text-foreground mb-3">
                <ShieldCheck className="size-4 text-primary" />
                <span>Our Technical Guarantee</span>
              </h4>
              <ul className="space-y-2.5">
                {assurances.map((assurance, index) => <li key={assurance.title} data-index={index} className="flex items-start gap-2 text-xs text-muted-foreground">
                    <div className="mt-1 size-1.5 shrink-0 rounded-full bg-primary" />
                    <span>
                      <strong className="text-foreground">{assurance.title}:</strong>{" "}
                      {assurance.description}
                    </span>
                  </li>)}
              </ul>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <Clock className="size-4 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">
                Need urgent technical assistance? Reach our hotline directly.
              </span>
            </div>
          </motion.div>

          {/* Right Column: Interactive Form Card */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.15
        }} className="lg:col-span-7">
            <Card className="border border-border bg-card shadow-md">
              <CardHeader className="border-b border-border/70 pb-6">
                <CardTitle className="font-default text-2xl font-bold text-card-foreground">
                  Contact Us - Antech Solutions
                </CardTitle>
                <CardDescription className="text-sm text-muted-foreground">
                  Complete the fields below to submit your technical specification, method assessment, or product inquiry.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <FormProvider formId="contact_form" sectionName="contact-form-section" defaultValues={{
                name: "",
                work_email: "",
                company_name: "",
                phone_number: "",
                message: ""
              }} validationRules={{
                name: {
                  required: true,
                  minLength: 2
                },
                work_email: {
                  required: true,
                  pattern: "email"
                },
                company_name: {
                  required: false
                },
                phone_number: {
                  required: false
                },
                message: {
                  required: true,
                  minLength: 10
                }
              }} submitText="Send Inquiry" successMessage="Thank you for reaching out to Antech Solutions. Your requirement has been routed to our technical engineering division." errorMessage="Unable to send your request. Please review the highlighted fields or contact us directly.">
                  <ContactFormInner />
                </FormProvider>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>;
}